import { useStore } from '@nanostores/react';
import { useDevHelperAccess, useDevHelperFeatureFlags } from '@/services/dev-helper/hooks';
import { useMemo } from 'react';
import { contextualParams, getApiClientErrorMessageFromError, hydraUiApi } from '@/services/api';
import { FEATURE_FLAG_DEFAULTS } from '@/services/auth/feature-flags';
import {
  $isDevHelperOpen,
  DEV_HELPER_TABS,
  isLocalDevHelperEnvironment,
  isProductionDevHelperEnvironment,
  openDevHelperTab,
  toggleDevHelper,
} from '@/services/dev-helper/store';
import type { LocalCommand } from './local-commands';
import { matchesLocalCommand } from './utils/search-commands';

// Flag names can match a query broadly (e.g. "campaign" matches many flags), so cap
// how many single-flag toggles we surface at once.
const MAX_FLAG_COMMANDS = 10;

async function updateFlags(accountId: string, flags: Record<string, boolean>): Promise<void> {
  await hydraUiApi.accounts.updateFeatureFlags({
    params: { path: { account_id: accountId } },
    body: flags,
  });
  window.location.reload();
}

function alertUpdateFailed(error: unknown): void {
  // Dev-only tool; alert avoids wiring snackbars outside the provider tree.
  window.alert(`Failed to update feature flags: ${getApiClientErrorMessageFromError(error) ?? String(error)}`);
}

const devHelperTabCommands: LocalCommand[] = DEV_HELPER_TABS.map(({ value, text }) => ({
  id: `dev:dev-helper-tab:${value}`,
  label: `Dev helper: ${text}`,
  icon: 'editor-code',
  description: `Open the ${text} tab in the dev helper panel.`,
  keywords: ['dev', 'helper', 'tab', 'debug', 'panel', ...text.toLowerCase().split(' ')],
  onSelect: () => {
    openDevHelperTab(value);
  },
}));

type DevCommandsOptions = {
  includeEmptyQuery?: boolean;
};

export function useDevCommands(query: string, options: DevCommandsOptions = {}): LocalCommand[] {
  const { includeEmptyQuery = false } = options;
  const params = contextualParams();
  const isLocalEnv = isLocalDevHelperEnvironment();
  const isProduction = isProductionDevHelperEnvironment();
  const canAccessDevHelper = useDevHelperAccess();
  const isOpen = useStore($isDevHelperOpen);

  const { data: featureFlags } = useDevHelperFeatureFlags(params.accountId, isLocalEnv);

  const accountId = params.accountId;

  const localCommands = useMemo(() => {
    if (!includeEmptyQuery && query.trim().length === 0) return [];
    if (!isLocalEnv || accountId == null) return [];
    // The commands label themselves Enable/Disable from the current values, so
    // there is nothing to offer until the flags query resolves. The PUT itself
    // is an overlay: keys left out of its body keep their value.
    if (featureFlags == null) return [];

    const commands: LocalCommand[] = devHelperTabCommands.filter(matchesLocalCommand(query));

    // Single-flag toggles: every flag known to hydra or already set on the
    // account, matched against the query. Query-gated so command mode's empty
    // query doesn't list a hundred flags.
    if (query.trim().length > 0) {
      const knownFlags = { ...FEATURE_FLAG_DEFAULTS, ...featureFlags };
      const flagCommands = Object.entries(knownFlags)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(
          ([flag, enabled]): LocalCommand => ({
            id: `dev:toggle-flag:${flag}`,
            label: `${enabled ? 'Disable' : 'Enable'} flag: ${flag}`,
            icon: 'skills',
            description: `Turn ${enabled ? 'off' : 'on'} the ${flag} feature flag for this account and reload.`,
            keywords: ['feature', 'flag', 'toggle', flag, ...flag.split(/[-_]/)],
            onSelect: async () => {
              try {
                await updateFlags(String(accountId), { ...featureFlags, [flag]: !enabled });
              } catch (error) {
                alertUpdateFailed(error);
              }
            },
          }),
        )
        .filter(matchesLocalCommand(query))
        .slice(0, MAX_FLAG_COMMANDS);

      commands.push(...flagCommands);
    }

    return commands;
  }, [accountId, featureFlags, includeEmptyQuery, query, isLocalEnv]);

  if (!includeEmptyQuery && query.trim().length === 0) return [];
  if (isProduction && canAccessDevHelper) {
    const command: LocalCommand = {
      id: 'dev:toggle-developer-tools',
      label: isOpen ? 'Close developer tools' : 'Open developer tools',
      icon: 'editor-code',
      description: 'Toggle the developer tools panel.',
      keywords: ['developer', 'tools', 'dev', 'helper', 'panel', 'debug'],
      onSelect: toggleDevHelper,
    };
    return [command].filter(matchesLocalCommand(query));
  }
  return localCommands;
}
